import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import './AdminShippingSettings.css';

function AdminShippingSettings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/shipping-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load shipping settings');
      setLoading(false);
    }
  };

  const handleEdit = (region) => {
    setEditMode({ ...editMode, [region]: true });
  };

  const handleCancel = (region) => {
    setEditMode({ ...editMode, [region]: false });
    fetchSettings(); // Reload to reset values
  };

  const handleChange = (region, field, value) => {
    setSettings(settings.map(s =>
      s.region === region ? { ...s, [field]: parseFloat(value) } : s
    ));
  };

  const handleSave = async (region) => {
    try {
      const setting = settings.find(s => s.region === region);
      const token = localStorage.getItem('adminToken');

      await axios.put(
        `/api/admin/shipping-settings/${region}`,
        {
          base_rate: setting.base_rate,
          per_item_rate: setting.per_item_rate,
          free_shipping_threshold: setting.free_shipping_threshold
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`${region} shipping settings updated successfully`);
      setEditMode({ ...editMode, [region]: false });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update ${region} settings`);
    }
  };

  if (loading) {
    return <Spinner text="Loading shipping settings..." />;
  }

  return (
    <div className="admin-shipping-settings">
      <div className="settings-header">
        <h1>Shipping Settings</h1>
        <p>Configure shipping rates and free shipping thresholds</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="settings-grid">
        {settings.map(setting => (
          <div key={setting.region} className="setting-card">
            <div className="card-header">
              <h2>United States</h2>
              {!editMode[setting.region] && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleEdit(setting.region)}
                >
                  Edit
                </button>
              )}
            </div>

            <div className="setting-fields">
              <div className="field-group">
                <label>Base Rate ($)</label>
                {editMode[setting.region] ? (
                  <input
                    type="number"
                    step="0.01"
                    value={setting.base_rate}
                    onChange={(e) => handleChange(setting.region, 'base_rate', e.target.value)}
                  />
                ) : (
                  <div className="field-value">${setting.base_rate.toFixed(2)}</div>
                )}
              </div>

              <div className="field-group">
                <label>Per Item Rate ($)</label>
                {editMode[setting.region] ? (
                  <input
                    type="number"
                    step="0.01"
                    value={setting.per_item_rate}
                    onChange={(e) => handleChange(setting.region, 'per_item_rate', e.target.value)}
                  />
                ) : (
                  <div className="field-value">${setting.per_item_rate.toFixed(2)}</div>
                )}
              </div>

              <div className="field-group">
                <label>Free Shipping Threshold ($)</label>
                {editMode[setting.region] ? (
                  <input
                    type="number"
                    step="0.01"
                    value={setting.free_shipping_threshold}
                    onChange={(e) => handleChange(setting.region, 'free_shipping_threshold', e.target.value)}
                  />
                ) : (
                  <div className="field-value">${setting.free_shipping_threshold.toFixed(2)}</div>
                )}
              </div>
            </div>

            {editMode[setting.region] && (
              <div className="card-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => handleSave(setting.region)}
                >
                  Save Changes
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handleCancel(setting.region)}
                >
                  Cancel
                </button>
              </div>
            )}

            {setting.updated_at && (
              <div className="last-updated">
                Last updated: {new Date(setting.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="info-section">
        <h3>How Shipping Calculation Works</h3>
        <ul>
          <li><strong>Base Rate:</strong> The starting shipping cost for the first item</li>
          <li><strong>Per Item Rate:</strong> Additional cost for each item after the first</li>
          <li><strong>Free Shipping Threshold:</strong> Order subtotal amount at which shipping becomes free</li>
        </ul>
        <p className="info-note">
          Example: With base rate $5.99 and per-item rate $1.50, a 3-item order would cost
          $5.99 + ($1.50 × 2) = $8.99 for shipping (unless it qualifies for free shipping).
        </p>
      </div>
    </div>
  );
}

export default AdminShippingSettings;
